import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  contractPeriodMsg,
  deviceListMsg,
  deviceShopCardHeaderMsg,
  deviceShopCardSection1HeaderMsg,
  deviceShopCardSection1Msg,
  deviceShopCardSection2HeaderMsg,
  deviceShopCardSection2Msg,
  deviceShopCardSection3HeaderMsg,
  deviceShopCardSection3Msg,
  deviceShopHeaderMsg,
  deviceShopListFooterMsg,
  deviceShopListHeaderMsg,
  deviceShopListInfoMsg,
  deviceShopSubtitleMsg,
  employeeOmaElisaMsg,
  monthMsg,
  productTypeMsg,
  shopMsg,
  systemErrorOccurredMsg,
  t,
} from '../../common/i18n/index.js';
import { getDeviceSelectionProductTypeLabel } from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../common/utils/deviceSelectionListUtilsCoCo.js';
import { getVatAsString } from '../../common/utils/taxUtils.js';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn.js';
import { HeroHeading, HeroHeadingType } from '../HeroHeading/index.js';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { BreadCrumbList } from '../BreadCrumbs/BreadCrumbs.js';

import './EoeDeviceStoreHomeSceneCoCo.scss';

interface EoeDeviceStoreHomeSceneCoCoProps {
  employeeProductLists: EmployeeProductListResponse[];
  isLoading: boolean;
  hasLoadError: boolean;
  onSelectDeviceList?: (list: EmployeeProductListResponse) => void;
}

const DeviceShopInstructions = () => (
  <>
    <h3>{t.XF0E(deviceShopCardHeaderMsg)}</h3>
    <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>{t.FA5L(deviceShopCardSection1HeaderMsg)}</h4>
    <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_0}`}>{t.JXVW(deviceShopCardSection1Msg)}</p>
    <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>{t.XVZ7(deviceShopCardSection2HeaderMsg)}</h4>
    <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_0}`}>{t.JGSO(deviceShopCardSection2Msg)}</p>
    <h4 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>{t.AWNU(deviceShopCardSection3HeaderMsg)}</h4>
    <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_0}`}>{t.WAEN(deviceShopCardSection3Msg, getVatAsString())}</p>
  </>
);

export const EoeDeviceStoreHomeSceneCoCo = ({
  employeeProductLists,
  isLoading,
  hasLoadError,
  onSelectDeviceList,
}: EoeDeviceStoreHomeSceneCoCoProps) => {
  const breadCrumbPaths: BreadCrumbList = [
    { name: t.H6JV(employeeOmaElisaMsg), path: paths.EMPLOYEE_HOME },
    { name: t.BZGP(shopMsg) },
  ];
  const columns = [
    { key: 'deviceList', label: t.Z0HF(deviceListMsg), width: '40%' },
    { key: 'productType', label: t.R6HP(productTypeMsg), width: '30%' },
    { key: 'contractPeriod', label: t.D55M(contractPeriodMsg), width: '30%' },
  ];
  const rows = employeeProductLists.map(list => ({
    deviceList: list.name ?? '',
    productType: getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(list.productType)),
    contractPeriod: list.contractTerms?.length
      ? list.contractTerms.map(term => `${term} ${t.XXVX(monthMsg)}`).join(', ')
      : '—',
  }));

  return (
    <div className="of-employee-device-store-coco">
      <HeroHeading
        center={true}
        fullScreen={true}
        breadCrumbPaths={breadCrumbPaths}
        heroHeadingType={HeroHeadingType.BLANK}
        title={t.LEO9(deviceShopHeaderMsg)}
        subtitle={t.KB1F(deviceShopSubtitleMsg)}
        smallSubtitle={true}
      />
      <DetailsWithCard cardImage="7B7Qg8qhvIR4tBWxqWTxJy/i.svg" cardContent={<DeviceShopInstructions />}>
        <h2>{t.OTQ4(deviceShopListHeaderMsg)}</h2>
        <p>{t.JNQG(deviceShopListInfoMsg)}</p>
        {hasLoadError && <p role="alert">{t.JCJF(systemErrorOccurredMsg)}</p>}
        {isLoading ? (
          <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.PADDING_8} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
            <CL.LoadingSpinner size="xl" />
          </div>
        ) : (
          <CL.Table
            className={dsClass.PADDING_VERTICAL_4}
            columns={columns}
            rows={rows}
            rowClicks={
              onSelectDeviceList ? employeeProductLists.map(list => () => onSelectDeviceList(list)) : undefined
            }
            hover={Boolean(onSelectDeviceList)}
          />
        )}
        {!isLoading && !hasLoadError && rows.length === 0 && <p>{t.TFX1('No device lists')}</p>}
        <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_BOTTOM_6}`}>{t.VBQK(deviceShopListFooterMsg)}</p>
      </DetailsWithCard>
    </div>
  );
};
